/**
 * @fileoverview Service Angular pour la gestion des Jobs (Worker + TaskList)
 *
 * Responsabilités :
 * - Charger la liste des Jobs depuis l'API
 * - Rafraîchir automatiquement après chaque réponse assistant (fin de chat)
 * - Supprimer un Job
 * - Exposer un observable `jobs$` pour les composants
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError, filter, skip, take, map, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { KngAssistantAiService } from './kng-assistant-ai.service';

// ============================================================================
// Types
// ============================================================================

/**
 * Résultat persisté d'un Worker Job (métriques + deliverable, pas de messages)
 */
export interface ClientWorkerResult {
  status: 'completed' | 'partial' | 'failed';
  summary: string;
  deliverable: string;
  iterations: number;
  confidence?: number;
  duration?: number;
  cost?: number;
  error?: string;
}

/**
 * Vue client aplatie d'un Job, retournée par GET /api/agent/jobs
 */
export interface ClientJob {
  id: string;
  type?: 'task-list' | 'worker';
  description?: string;
  personaWorkerKey?: string;
  personaResultKey?: string;
  maxIterations?: number;
  result?: ClientWorkerResult;
  updatedAt: string;
}

// ============================================================================
// Service
// ============================================================================

@Injectable({
  providedIn: 'root'
})
export class KngJobsService {
  private $http = inject(HttpClient);
  private $agents = inject(KngAssistantAiService);

  private readonly headers = {
    'Content-Type': 'application/json',
    'ngsw-bypass': 'true',
    'Cache-Control': 'no-cache',
  };

  //
  // Observable principal — tous les jobs de l'utilisateur
  private jobsSubject = new BehaviorSubject<ClientJob[]>([]);
  jobs$ = this.jobsSubject.asObservable();

  constructor() {
    //
    // Auto-refresh : recharger les jobs à chaque fin de réponse assistant
    this.$agents.state$.pipe(
      filter(state => state.status === 'end')
    ).subscribe(() => {
      this.list().subscribe();
    });

    //
    // Auto-refresh : recharger les jobs lors d'un step worker (worker_started, worker_completed, worker_failed)
    // Debounce 1.5s pour éviter les appels multiples pendant le streaming
    this.$agents.state$.pipe(
      filter(state => state.status === 'running' && !!state.steps?.length),
      map(state => state.steps!.filter(s => s.agent === 'worker' && s.data?.type).pop()),
      filter(step => !!step),
      map(step => step!.data.type as string),
      distinctUntilChanged(),
      debounceTime(1500),
    ).subscribe(() => {
      this.list().subscribe();
    });
  }

  // ============================================================================
  // Public API
  // ============================================================================

  /**
   * Charge la liste des Jobs depuis l'API
   * Met à jour jobs$ automatiquement
   */
  list(): Observable<ClientJob[]> {
    return this.$http.get<ClientJob[]>('/api/agent/jobs', {
      withCredentials: true,
      headers: this.headers
    }).pipe(
      tap(jobs => this.jobsSubject.next(jobs)),
      catchError(err => {
        console.error('❌ KngJobsService.list():', err);
        return of([]);
      })
    );
  }

  /**
   * Supprime un Job
   */
  delete(jobId: string): Observable<any> {
    return this.$http.delete(`/api/agent/jobs/${jobId}`, {
      withCredentials: true,
      headers: this.headers
    }).pipe(
      tap(() => {
        //
        // Mettre à jour le cache local immédiatement
        const current = this.jobsSubject.value;
        this.jobsSubject.next(current.filter(j => j.id !== jobId));
      }),
      catchError(err => {
        console.error('❌ KngJobsService.delete():', err);
        return of(null);
      })
    );
  }

  /**
   * Observable "loaded" pour le loader initial
   * Skip la valeur initiale [] et prend la première valeur après l'appel API
   */
  get jobsLoaded$(): Observable<ClientJob[]> {
    return this.jobs$.pipe(
      skip(1),
      take(1)
    );
  }
}
